import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import type { Session, SessionDetail, PillarsResponse, FocusOverview, Plan } from "../types";
import SessionsTable from "../components/dashboard/SessionsTable";
import SessionSidePanel from "../components/dashboard/SessionSidePanel";
import ErrorTrendChart from "../components/dashboard/charts/ErrorTrendChart";
import ScoresTrendChart from "../components/dashboard/charts/ScoresTrendChart";
import ErrorBreakdownChart from "../components/dashboard/charts/ErrorBreakdownChart";
import PillarScorecard from "../components/dashboard/charts/PillarScorecard";
import TopMistakesCard from "../components/dashboard/TopMistakesCard";
import GenerationLoader from "../components/GenerationLoader";

function formatPracticeTime(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.round((totalSeconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function StatTile({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="card px-5 py-4 animate-rise">
      <div className="font-display font-semibold tracking-[-0.02em] text-[1.75rem] text-ink leading-tight tabular-nums">{value}</div>
      <div className="section-label mt-1">{label}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSessionId = (() => {
    const raw = searchParams.get("session");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [details, setDetails] = useState<SessionDetail[]>([]);
  const [pillars, setPillars] = useState<PillarsResponse | null>(null);
  const [focus, setFocus] = useState<FocusOverview | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(initialSessionId);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<Plan | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      const all = await api.getSessions(50).catch(() => null);
      if (cancelled || !all) return;
      setSessions(all);
      const ended = all.filter((s) => s.ended_at).slice(0, 10).reverse();
      const [fetched, pillarScores, focusOverview] = await Promise.all([
        Promise.all(ended.map((s) => api.getSession(s.id))),
        api.getPillars().catch(() => null),
        api.getFocus().catch(() => null),
      ]);
      if (cancelled) return;
      setDetails(fetched);
      setPillars(pillarScores);
      setFocus(focusOverview);
      setLoading(false);
      if (all.some((s) => s.analysis_complete === false)) {
        pollTimer = setTimeout(load, 3000);
      }
    };

    api.getMe().then((me) => setPlan(me.plan)).catch(() => {});
    load();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, []);

  const clearSessionParam = () => {
    if (searchParams.has("session")) {
      searchParams.delete("session");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleDeleted = (deletedId: number) => {
    setSessions((prev) => prev.filter((s) => s.id !== deletedId));
    setDetails((prev) => prev.filter((d) => d.session.id !== deletedId));
    setSelectedSessionId(null);
    clearSessionParam();
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <GenerationLoader
          messages={["Loading your sessions...", "Fetching your stats...", "Calculating scores..."]}
          subtitle=""
        />
      </div>
    );
  }

  const practiceSeconds = sessions.reduce((acc, s) => acc + (s.duration_s ?? 0), 0);
  const scored = details.filter((d) => d.metrics);
  const avgFluency = scored.length
    ? (scored.reduce((acc, d) => acc + (d.metrics!.fluency_score ?? 0), 0) / scored.length).toFixed(1)
    : "—";
  const avgComplexity = scored.length
    ? (scored.reduce((acc, d) => acc + (d.metrics!.complexity_score ?? 0), 0) / scored.length).toFixed(1)
    : "—";

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 py-10">
        <header className="mb-8 animate-rise">
          <h1>Progress</h1>
          <p className="text-text-tertiary text-[0.9375rem] mt-1">How your Italian is coming along.</p>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <StatTile value={sessions.length} label="Sessions" />
          <StatTile value={formatPracticeTime(practiceSeconds)} label="Practice time" />
          <StatTile value={avgFluency} label="Avg fluency · last 10" />
          <StatTile value={avgComplexity} label="Avg complexity · last 10" />
        </div>

        {focus && <TopMistakesCard data={focus} />}

        {pillars && (
          <div className="card p-6 sm:p-8 animate-rise mb-8">
            <PillarScorecard data={pillars} />
          </div>
        )}

        {details.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
            <div className="card p-6 animate-rise [animation-delay:0.05s]">
              <h4 className="section-label mb-4">Error rate · last 10</h4>
              <ErrorTrendChart details={details} />
            </div>
            <div className="card p-6 animate-rise [animation-delay:0.1s]">
              <h4 className="section-label mb-6">Fluency &amp; complexity · last 10</h4>
              <ScoresTrendChart details={details} />
            </div>
            <div className="card p-6 animate-rise [animation-delay:0.15s] md:col-span-2 xl:col-span-1">
              <h4 className="section-label mb-6">Error breakdown · last 10</h4>
              <ErrorBreakdownChart details={details} />
            </div>
          </div>
        )}

        <div className="card p-6 animate-rise [animation-delay:0.2s]">
          <h4 className="section-label mb-4">All sessions</h4>
          <SessionsTable sessions={sessions} onSelect={setSelectedSessionId} />
        </div>
      </div>

      {selectedSessionId !== null && (
        <SessionSidePanel
          sessionId={selectedSessionId}
          sessionNumber={[...sessions].sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()).findIndex((s) => s.id === selectedSessionId) + 1 || undefined}
          onClose={() => {
            setSelectedSessionId(null);
            clearSessionParam();
          }}
          onDeleted={handleDeleted}
          isTrial={plan === "trial"}
        />
      )}
    </div>
  );
}
